// Portal Gestão DNA · Hook + Sensitive helpers (V4.51)
// - Carrega scope + permissões sensíveis do backend (/api/portal-gestao/me/scope)
// - Registra auditoria (login, page-view, view sensível, edit, export)
// - Expõe <SensitiveField verb="cpf" value={...} /> para mascarar no UI
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const PortalGestaoContext = createContext(null);

export function PortalGestaoProvider({ children }) {
  const [scope, setScope] = useState({});
  const [sensitive, setSensitive] = useState({
    cpf: false, endereco: false, telefone: false, saude: false,
    notas: false, financeiro: false, exportar: false,
  });
  const [isAdminBypass, setIsAdminBypass] = useState(false);
  const [dnagoAutoGrant, setDnagoAutoGrant] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/portal-gestao/me/scope');
      setScope(data?.scope || {});
      setSensitive(data?.sensitive || {});
      setIsAdminBypass(!!data?.is_admin_bypass);
      setDnagoAutoGrant(!!data?.dnago_auto_grant);
    } catch (_) {
      // sem sessão / sem perm — mantém defaults
    } finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('token')) refresh();
    else setLoaded(true);
  }, [refresh]);

  const logAudit = useCallback(async (payload) => {
    try { await api.post('/portal-gestao/audit', payload); } catch (_) { /* fire-and-forget */ }
  }, []);

  const canSensitive = useCallback((verb) => !!(isAdminBypass || sensitive?.[verb]), [isAdminBypass, sensitive]);

  const value = { scope, sensitive, isAdminBypass, dnagoAutoGrant, loaded, refresh, logAudit, canSensitive };
  return <PortalGestaoContext.Provider value={value}>{children}</PortalGestaoContext.Provider>;
}

export function usePortalGestao() {
  const ctx = useContext(PortalGestaoContext);
  if (!ctx) {
    // FORA do Portal Gestão (contextos V2/DNAConnect): comportamento transparente.
    // canSensitive() retorna true → <SensitiveField> não mascara e não loga.
    return {
      scope: {}, sensitive: {}, isAdminBypass: false, dnagoAutoGrant: false, loaded: false,
      inPortal: false,
      refresh: () => {}, logAudit: () => {}, canSensitive: () => true,
    };
  }
  return { ...ctx, inPortal: true };
}

/**
 * <SensitiveField verb="cpf" value={aluno.cpf} />
 * Mascara automaticamente quando o usuário não tem `dados_sensiveis.<verb>`.
 * Ao renderizar o valor real, dispara audit `view_sensitive`.
 */
export function SensitiveField({ verb, value, mask = '•••', onceKey, targetType, targetId }) {
  const { canSensitive, logAudit, inPortal } = usePortalGestao();
  const ok = canSensitive(verb);
  useEffect(() => {
    // Só loga view_sensitive DENTRO do Portal Gestão
    if (!ok || !onceKey || !inPortal) return;
    logAudit({ action: 'view_sensitive', target_type: targetType || null, target_id: targetId || null, meta: { verb, key: onceKey } });
  }, [ok, onceKey, logAudit, targetType, targetId, verb, inPortal]);
  if (!ok) return <span className="text-slate-400 select-none" data-testid={`sensitive-mask-${verb}`}>{mask}</span>;
  return <>{value ?? '—'}</>;
}
